import type {
    IDocumentExtractedFieldItem,
    IExtractedPropertyValue,
} from "@/api/documents/documents.types";
import { confirmedDeleteFingerprint } from "@/api/documents/deleteFingerprint";
import { Button } from "@/components/ui/button";
import { getPropertyDisplayValue } from "@/utils/fields";
import { Chip, Stack, Typography } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { Check, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
} from "../StyledAccordion";
import { EditablePropertyRow } from "./EditablePropertyRow";

interface IPropertyRowProps {
    property: IExtractedPropertyValue;
    pageNavigationPluginInstance: PageNavigationPlugin;
}

const PropertyRow = ({
    property,
    pageNavigationPluginInstance,
}: IPropertyRowProps) => {
    const handleChipClick = useCallback(() => {
        if (!property.page_number) return;
        pageNavigationPluginInstance?.jumpToPage(property.page_number - 1);
    }, [property.page_number, pageNavigationPluginInstance]);

    return (
        <Stack
            direction="row"
            gap={1}
            sx={{
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
            }}
        >
            <Typography variant="body2">{property.property_name}</Typography>
            <Stack direction="row" gap={1.5} sx={{ alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                    {getPropertyDisplayValue(property.value ?? undefined)}
                </Typography>
                {property.page_number && (
                    <Chip
                        label={`p. ${property.page_number}`}
                        onClick={handleChipClick}
                        size="small"
                        color="tertiary"
                        sx={{ height: 20, fontSize: "0.75rem" }}
                    />
                )}
            </Stack>
        </Stack>
    );
};

interface IProps {
    item: IDocumentExtractedFieldItem;
    pageNavigationPluginInstance: PageNavigationPlugin;
    expanded?: boolean;
    onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
    isEditable?: boolean;
    onSave?: (propertyId: number, value: string | null) => void;
    onDelete?: (propertyId: number) => void;
    onDeleteItem?: (expectedDeleteFingerprint: string) => void;
}

export const DynamicArrayItem = ({
    item,
    pageNavigationPluginInstance,
    expanded,
    onChange,
    isEditable = false,
    onSave,
    onDelete,
    onDeleteItem,
}: IProps) => {
    // Get the first property with a page number for the summary chip
    const pageNumber = item.properties.find((p) => p.page_number)?.page_number;

    const handleChipClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event?.stopPropagation();
            if (!pageNumber) return;
            pageNavigationPluginInstance?.jumpToPage(pageNumber - 1);
        },
        [pageNumber, pageNavigationPluginInstance]
    );

    const deleteFingerprint = confirmedDeleteFingerprint(item);
    const [armedFingerprint, setArmedFingerprint] = useState<string | null>(
        null
    );
    const isDeleteConfirming =
        armedFingerprint !== null && armedFingerprint === deleteFingerprint;
    const deleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
        },
        []
    );

    const handleDeleteItem = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!deleteFingerprint || !onDeleteItem) return;
            if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
            if (!isDeleteConfirming) {
                setArmedFingerprint(deleteFingerprint);
                deleteTimerRef.current = setTimeout(
                    () => setArmedFingerprint(null),
                    2000
                );
            } else {
                setArmedFingerprint(null);
                onDeleteItem(deleteFingerprint);
            }
        },
        [isDeleteConfirming, onDeleteItem, deleteFingerprint]
    );

    const handleSave = useCallback(
        (propertyId: number, value: string | null) => {
            onSave?.(propertyId, value);
        },
        [onSave]
    );

    const handleDelete = useCallback(
        (propertyId: number) => {
            onDelete?.(propertyId);
        },
        [onDelete]
    );

    return (
        <Accordion
            expanded={expanded}
            onChange={onChange}
            slotProps={{ transition: { timeout: 0 } }}
        >
            <AccordionSummary>
                <Stack
                    direction="row"
                    gap={1}
                    sx={{
                        justifyContent: "space-between",
                        width: "100%",
                    }}
                >
                    <Stack>
                        <Typography variant="body2">
                            {item.display_name ||
                                `Item #${item.array_index + 1}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {item.properties.length} properties
                        </Typography>
                    </Stack>
                    <Stack
                        direction="row"
                        gap={0.5}
                        sx={{ alignItems: "center" }}
                    >
                        {pageNumber && (
                            <Chip
                                label={`p. ${pageNumber}`}
                                size="small"
                                color="tertiary"
                                sx={{ height: 20, fontSize: "0.75rem" }}
                                onClick={handleChipClick}
                            />
                        )}
                        {isEditable && onDeleteItem && (
                            <Button
                                variant={
                                    isDeleteConfirming ? "destructive" : "ghost"
                                }
                                size="icon-xs"
                                onClick={handleDeleteItem}
                                disabled={deleteFingerprint === null}
                                title={
                                    deleteFingerprint === null
                                        ? "Refresh to delete this item"
                                        : undefined
                                }
                                aria-label={
                                    isDeleteConfirming
                                        ? `Confirm delete ${item.display_name || `Item #${item.array_index + 1}`}`
                                        : `Delete ${item.display_name || `Item #${item.array_index + 1}`}`
                                }
                            >
                                {isDeleteConfirming ? (
                                    <Check size={12} aria-hidden />
                                ) : (
                                    <Trash2 size={12} aria-hidden />
                                )}
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={isEditable ? 0 : 2}>
                    {item.properties.map((property) =>
                        isEditable ? (
                            <EditablePropertyRow
                                key={property.id}
                                property={property}
                                isEditable={isEditable}
                                onSave={handleSave}
                                onDelete={handleDelete}
                                pageNavigationPluginInstance={
                                    pageNavigationPluginInstance
                                }
                            />
                        ) : (
                            <PropertyRow
                                key={property.id}
                                property={property}
                                pageNavigationPluginInstance={
                                    pageNavigationPluginInstance
                                }
                            />
                        )
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};
